import {isNil} from '#lit/utils/index.js';
import {dig} from '#lit/utils/objects.js';
import type {AnyObject} from '#types';

import {hashString} from './strings.js';

type SortCompareFn<T> = (a: T, b: T) => number;

/**
 * Get value by string hash
 */
export function getValueByHash<T>(arr: T[], str: string): T {
  if (arr.length === 0) {
    throw new Error(
      'Cannot get value from empty array. Please provide a non-empty array.'
    );
  }
  const hash = hashString(str);
  const value = arr[hash % arr.length];
  return value!;
}

/**
 * Generates a range given a head and tail
 */
export function* range(head: number, tail: number) {
  for (let i = head; i <= tail; i++) {
    yield i;
  }
}

/**
 * Group an array of objects by a key
 */
export function groupBy<T extends AnyObject>(
  arr: T[],
  path: string
): Map<string, T[]> {
  const grouped = new Map();
  const pathParts = path.split('.');

  for (const item of arr) {
    const key = dig(item, pathParts);
    if (!isNil(key)) {
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(item);
    }
  }

  return grouped;
}

/**
 * Group an array of objects by a key and sort each group
 *
 * @param arr - The array to group
 * @param path - The key to group by
 * @param sortFn - The function to sort each group
 * @returns A map of grouped items
 */
export function groupByAndSort<T extends AnyObject>(
  arr: T[],
  path: string,
  sortFn: SortCompareFn<T>
): Map<string, T[]> {
  const grouped = groupBy(arr, path);
  grouped.forEach(group => {
    group.sort(sortFn);
  });
  return grouped;
}
